// src/pages/billing/BillingPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { projectService } from '../../services/project.service';
import { billingService } from '../../services/billing.service';
import { billingSnapshotService } from '../../services/billingSnapshot.service';
import { billingAdjustmentsService } from '../../services/billingAdjustments.service';
import { fieldPaymentService } from '../../services/fieldPayment.service';
import { getLead } from '../../services/lead.service';
import * as UserService from '../../services/user.service';
import { organizationService } from '../../services/organization.service';
import { invoiceService } from '../../services/invoice.service';
import { emailService } from '../../services/email.service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  BillingPeriod, 
  BillingSnapshot, 
  FieldStaffPayment,
  WeeklyBillingReport,
  DeterminationChange
} from '../../types/billing.types';
import { Project } from '../../types/project.types';
import { User } from '../../types/user.types';
import { format, startOfWeek, endOfWeek, subWeeks, addDays, isEqual } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import {
  FileText, Download, ChevronLeft, ChevronRight,
  ArrowUpCircle, ArrowDownCircle, Receipt
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatSafeDate } from '../../utils/dateUtils';

export default function BillingPage() {
  const { userProfile } = useAuth();
  const { projects, selectedProjectId, selectedProject } = useProject();
  const [currentWeekBilling, setCurrentWeekBilling] = useState<BillingPeriod | null>(null);
  const [determinationChanges, setDeterminationChanges] = useState<DeterminationChange[]>([]);
  const [previousWeeks, setPreviousWeeks] = useState<BillingPeriod[]>([]);
  const [fieldPayments, setFieldPayments] = useState<FieldStaffPayment[]>([]);
  const [billingSnapshots, setBillingSnapshots] = useState<BillingSnapshot[]>([]);
  const [weeklyReports, setWeeklyReports] = useState<WeeklyBillingReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'billing' | 'field'>('billing');
  const [selectedWeek, setSelectedWeek] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [billRate, setBillRate] = useState<number>(45);
  const [netAdjustmentImpact, setNetAdjustmentImpact] = useState<number>(0);
  const [fieldUsers, setFieldUsers] = useState<User[]>([]);
  const [selectedFieldUserId, setSelectedFieldUserId] = useState<string>('all');
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState<boolean>(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Check user permissions
  const canViewBilling = ['provider_admin', 'provider_user', 'client_admin'].includes(userProfile?.role || '');
  const canViewFieldPayments = ['provider_admin', 'provider_user'].includes(userProfile?.role || '');
  const isFieldUser = userProfile?.role === 'field';
  // Fiscal privacy: provider_admin OR primary admin of an org can see bill rates, revenue, and profits
  const canViewFiscalDetails = userProfile?.role === 'provider_admin' || isPrimaryAdmin;

  // Check if user is primary admin of their organization
  useEffect(() => {
    const checkPrimaryAdmin = async () => {
      if (!userProfile?.organizationId) {
        setIsPrimaryAdmin(false);
        return;
      }
      try {
        const org = await organizationService.get(userProfile.organizationId);
        setIsPrimaryAdmin(org?.primaryAdminId === userProfile.uid);
      } catch (error) {
        console.error('Error checking primary admin status:', error);
        setIsPrimaryAdmin(false);
      }
    };
    checkPrimaryAdmin();
  }, [userProfile?.organizationId, userProfile?.uid]);

  const isCurrentWeek = isEqual(
    startOfWeek(selectedWeek, { weekStartsOn: 1 }), 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  useEffect(() => {
    if (selectedProjectId) {
      loadBillingData();
      if (viewMode === 'field' && canViewFieldPayments) {
        loadFieldUsers();
      }
    }
  }, [selectedProjectId, selectedWeek, viewMode]);

  const loadFieldUsers = async () => {
    if (!selectedProjectId) return;
    
    try {
      const users = await UserService.getUsers({
        projectId: selectedProjectId,
        role: 'field'
      });
      setFieldUsers(users);
    } catch (error) {
      console.error('Error loading field users:', error);
    }
  };

  const fetchUserName = async (userId: string): Promise<string> => {
    try {
      const user = await UserService.getUserById(userId);
      if (user) {
        return user.name || user.email || 'Unknown User';
      }
      return 'Unknown User';
    } catch (error) {
      console.error('Error fetching user name:', error);
      return 'Unknown User';
    }
  };

  const fetchLeadParentName = async (leadId: string): Promise<string> => {
    try {
      const lead = await getLead(leadId);
      return lead?.parentName || 'Unknown';
    } catch (error) {
      console.error('Error fetching lead parent name:', error);
      return 'Unknown';
    }
  };

  const loadBillingData = async () => {
    if (!selectedProjectId || !userProfile) return;
    
    setLoading(true);
    try {
      const currentProject = await projectService.getProject(selectedProjectId);
      const projectBillRate = currentProject?.settings.billingInfo.ratePerValidLead || 45;
      setBillRate(projectBillRate);

      const currentWeekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
      const currentWeekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
      
      // Load field payments
      const payments = await fieldPaymentService.getProjectPayments(selectedProjectId);
      setFieldPayments(payments);
      
      // Load billing snapshots
      const snapshots = await billingSnapshotService.getProjectSnapshots(selectedProjectId);
      setBillingSnapshots(snapshots);
      
      // Check if we should create a snapshot for past weeks
      const isPastWeekLockTime = (weekStart: Date) => {
        const now = new Date();
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        let lockTime = new Date(weekEnd);
        lockTime.setHours(23, 59, 59, 999);
        lockTime = fromZonedTime(lockTime, 'America/Los_Angeles');
        return now > lockTime;
      };
      
      // Load current week billing
      let billingData: BillingPeriod;
      
      if (isPastWeekLockTime(currentWeekStart) && !isCurrentWeek) {
        const snapshot = snapshots.find(s => 
          format(s.weekStart, 'yyyy-MM-dd') === format(currentWeekStart, 'yyyy-MM-dd')
        );
        
        if (snapshot) {
          billingData = {
            projectId: selectedProjectId,
            projectName: currentProject?.name,
            periodStart: currentWeekStart,
            periodEnd: currentWeekEnd,
            leads: snapshot.leads.map(snapLead => ({
              leadId: snapLead.leadId,
              parentName: snapLead.parentName,
              children: snapLead.children,
              capturedBy: snapLead.capturedBy,
              capturedByName: snapLead.capturedByName,
              capturedDate: snapLead.capturedDate,
              currentDetermination: snapLead.currentDetermination,
              determinationHistory: [],
              enrolled: snapLead.enrolled,
              enrolledStudents: snapLead.enrolledStudents,
              status: snapLead.status,
              billableAmount: snapLead.children.length * billRate
            })),
            summary: snapshot.summary,
            adjustments: []
          };
        } else {
          billingData = await billingService.calculateWeeklyBilling(selectedProjectId, currentWeekStart);
          await billingSnapshotService.createSnapshotFromBilling(billingData);
        }
      } else {
        billingData = await billingService.calculateWeeklyBilling(selectedProjectId, currentWeekStart);
      }
      
      setCurrentWeekBilling(billingData);
      
      // Load determination changes
      const adjustmentsData = await billingAdjustmentsService.getBillingPeriodAdjustments(
        selectedProjectId,
        currentWeekStart,
        currentWeekEnd
      );
      setDeterminationChanges(adjustmentsData.adjustments);
      
      // Net billable STUDENT impact from this week's determination changes,
      // matching the invoice's carry-over math: any change INTO valid adds its
      // students, any change OUT of valid subtracts them. The prior version
      // only counted valid↔invalid flips and used the flat per-lead
      // `billingImpact`, so it ignored every pending→valid change — which made
      // the "Total Billed" card read far too low (e.g. $250 when 15 families /
      // 31 students had just been validated = $7,750).
      let netImpact = 0;
      for (const adj of adjustmentsData.adjustments) {
        const students = (adj as any).students ?? 1;
        if (adj.newDetermination === 'valid' && adj.previousDetermination !== 'valid') {
          netImpact += students;
        } else if (adj.previousDetermination === 'valid' && adj.newDetermination !== 'valid') {
          netImpact -= students;
        }
      }
      setNetAdjustmentImpact(netImpact);
      
      // Load previous weeks
      const previousWeeksData: BillingPeriod[] = [];
      for (let i = 1; i <= 4; i++) {
        const weekStart = subWeeks(currentWeekStart, i);
        const weekSnapshot = snapshots.find(s => 
          format(s.weekStart, 'yyyy-MM-dd') === format(weekStart, 'yyyy-MM-dd')
        );
        
        if (weekSnapshot) {
          previousWeeksData.push({
            projectId: selectedProjectId,
            projectName: currentProject?.name,
            periodStart: weekStart,
            periodEnd: endOfWeek(weekStart, { weekStartsOn: 1 }),
            leads: weekSnapshot.leads.map(snapLead => ({
              leadId: snapLead.leadId,
              parentName: snapLead.parentName,
              children: snapLead.children,
              capturedBy: snapLead.capturedBy,
              capturedByName: snapLead.capturedByName,
              capturedDate: snapLead.capturedDate,
              currentDetermination: snapLead.currentDetermination,
              determinationHistory: [],
              enrolled: snapLead.enrolled,
              enrolledStudents: snapLead.enrolledStudents,
              status: snapLead.status,
              billableAmount: snapLead.children.length * billRate
            })),
            summary: weekSnapshot.summary,
            adjustments: []
          });
        } else {
          const weekData = await billingService.calculateWeeklyBilling(selectedProjectId, weekStart);
          previousWeeksData.push(weekData);
        }
      }
      setPreviousWeeks(previousWeeksData);
      
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Provider: generate (or refresh) the selected week's staff payment records.
  const handleGeneratePayments = async () => {
    if (!selectedProjectId) return;
    if (!window.confirm("Generate staff payment records for this week? This computes each contractor's 50% weekly pay and 50% holdback from valid students.")) return;
    try {
      await fieldPaymentService.processWeeklyPayments(selectedProjectId, startOfWeek(selectedWeek, { weekStartsOn: 1 }));
      toast.success('Staff payments generated for this week.');
      loadBillingData();
    } catch (e) {
      console.error('Error generating payments:', e);
      toast.error('Failed to generate staff payments.');
    }
  };

  // Provider: release a contractor's held 50% once they hit 20% conversion.
  const handleReleaseHoldback = async (paymentId: string) => {
    if (!window.confirm('Release the held 50% for this contractor? Do this once they have reached 20% conversion.')) return;
    try {
      await fieldPaymentService.releaseHoldback(paymentId);
      toast.success('Holdback released.');
      loadBillingData();
    } catch (e) {
      console.error('Error releasing holdback:', e);
      toast.error('Failed to release holdback.');
    }
  };

  const exportToExcel = () => {
    if (!currentWeekBilling) return;

    const wb = XLSX.utils.book_new();
    const currentProject = projects.find(p => p.id === selectedProjectId);
    
    // Summary Sheet
    const summaryData = [
      ['Billing Summary', format(selectedWeek, 'MMM d, yyyy')],
      ['Project', currentProject?.name || ''],
      [''],
      ['Total Leads', currentWeekBilling.summary.totalLeads],
      ['Valid Leads', currentWeekBilling.summary.validLeads],
      ['Invalid Leads', currentWeekBilling.summary.invalidLeads],
      ['Pending Leads', currentWeekBilling.summary.pendingLeads],
      ['Total Children', currentWeekBilling.summary.totalChildren || 0],
      ['Valid Students', currentWeekBilling.summary.validStudents || 0],
      ['Enrolled Students', currentWeekBilling.summary.enrolledStudents || 0],
      ['Conversion Rate', `${(currentWeekBilling.summary.conversionRate || 0).toFixed(1)}%`],
      [''],
      ['Bill Rate per Student', `$${billRate}`],
      ['Total Billable Students', currentWeekBilling.summary.totalBillable],
      ['Determination Adjustments', determinationChanges.length],
      ['Net Adjustment Impact', netAdjustmentImpact],
      ['Total Billed', `$${((currentWeekBilling.summary.totalBillable || 0) * billRate + netAdjustmentImpact * billRate).toFixed(2)}`]
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
    
    // Leads Detail Sheet
    const leadsData = currentWeekBilling.leads.map(lead => ({
      'Parent Name': lead.parentName,
      'Email': '', // Add if available in lead data
      'Phone': '', // Add if available in lead data
      'Address': '', // Add if available in lead data
      'Children': lead.children.map(c => c.name).join(', '),
      'Number of Children': lead.children.length,
      'Child Details': lead.children.map(c => `${c.name} (Grade ${c.grade}, DOB: ${c.dateOfBirth})`).join('; '),
      'Captured By': lead.capturedByName,
      'Captured Date': formatSafeDate(lead.capturedDate, 'MM/dd/yyyy'),
      'Determination': lead.currentDetermination,
      'Status': lead.status,
      'Enrolled': lead.enrolled ? 'Yes' : 'No',
      'Enrolled Students': lead.enrolledStudents || 0,
      'Billable Amount': lead.billableAmount || 0
    }));
    
    const leadsSheet = XLSX.utils.json_to_sheet(leadsData);
    XLSX.utils.book_append_sheet(wb, leadsSheet, 'Leads Detail');
    
    // Determination Changes Sheet
    if (determinationChanges.length > 0) {
      const changesData = determinationChanges.map(change => ({
        'Lead ID': change.leadId,
        'Parent Name': change.parentName || 'Unknown',
        'Changed By': change.changedByName || change.changedBy,
        'Date Changed': format(change.changedAt.toDate(), 'MM/dd/yyyy HH:mm'),
        'Previous': change.previousDetermination,
        'New': change.newDetermination,
        'Billing Impact': change.billingImpact,
        'Notes': change.notes || ''
      }));
      
      const changesSheet = XLSX.utils.json_to_sheet(changesData);
      XLSX.utils.book_append_sheet(wb, changesSheet, 'Determination Changes');
    }
    
    // Save file
    XLSX.writeFile(wb, `billing_${format(selectedWeek, 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel file downloaded');
  };

  const exportToPDF = () => {
    if (!currentWeekBilling) return;

    const doc = new jsPDF();
    const currentProject = projects.find(p => p.id === selectedProjectId);
    
    // Header with logo/styling
    doc.setFillColor(65, 105, 225);
    doc.rect(0, 0, 210, 40, 'F');
    
    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('Billing Report', 14, 20);
    
    // Subtitle
    doc.setFontSize(14);
    doc.text(`${currentProject?.name || 'Unknown Project'}`, 14, 30);
    
    // Date range
    doc.setFontSize(10);
    doc.text(`Week: ${format(selectedWeek, 'MMM d')} - ${format(endOfWeek(selectedWeek, { weekStartsOn: 1 }), 'MMM d, yyyy')}`, 14, 36);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Summary Metrics
    doc.setFontSize(16);
    doc.text('Summary Metrics', 14, 55);
    
    const summaryData = [
      ['Metric', 'Value'],
      ['Total Leads', currentWeekBilling.summary.totalLeads.toString()],
      ['Valid Leads', currentWeekBilling.summary.validLeads.toString()],
      ['Invalid Leads', currentWeekBilling.summary.invalidLeads.toString()],
      ['Pending Leads', currentWeekBilling.summary.pendingLeads.toString()],
      ['Total Children', (currentWeekBilling.summary.totalChildren || 0).toString()],
      ['Valid Students', (currentWeekBilling.summary.validStudents || 0).toString()],
      ['Enrolled Students', (currentWeekBilling.summary.enrolledStudents || 0).toString()],
      ['Conversion Rate', `${(currentWeekBilling.summary.conversionRate || 0).toFixed(1)}%`],
      ['Total Billable', currentWeekBilling.summary.totalBillable.toString()],
      ['Net Adjustment Impact', netAdjustmentImpact.toString()],
      ['Total Billed', `$${((currentWeekBilling.summary.totalBillable || 0) * billRate + netAdjustmentImpact * billRate).toFixed(2)}`]
    ];
    
    autoTable(doc, {
      head: [summaryData[0]],
      body: summaryData.slice(1),
      startY: 60,
      theme: 'grid',
      headStyles: { fillColor: [65, 105, 225] }
    });
    
    // Lead Details
    const finalY = (doc as any).lastAutoTable.finalY || 60;
    doc.setFontSize(16);
    doc.text('Lead Details', 14, finalY + 15);
    
    const leadsData = currentWeekBilling.leads.map(lead => [
      lead.parentName,
      lead.children.map(c => c.name).join(', '),
      lead.capturedByName,
      formatSafeDate(lead.capturedDate, 'MM/dd/yyyy'),
      lead.currentDetermination,
      lead.status
    ]);
    
    autoTable(doc, {
      head: [['Parent Name', 'Children', 'Captured By', 'Date', 'Determination', 'Status']],
      body: leadsData,
      startY: finalY + 20,
      theme: 'striped',
      headStyles: { fillColor: [65, 105, 225] },
      styles: { fontSize: 8 }
    });
    
    // Determination Changes (if any)
    if (determinationChanges.length > 0) {
      const leadsTableFinalY = (doc as any).lastAutoTable.finalY || 60;
      
      if (leadsTableFinalY > 200) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text('Determination Changes', 14, 20);
        
        const changesData = determinationChanges.map(change => [
          change.parentName || 'Unknown',
          change.previousDetermination,
          change.newDetermination,
          change.changedByName || change.changedBy,
          format(change.changedAt.toDate(), 'MM/dd HH:mm')
        ]);
        
        autoTable(doc, {
          head: [['Parent Name', 'Previous', 'New', 'Changed By', 'Date']],
          body: changesData,
          startY: 25,
          theme: 'striped',
          headStyles: { fillColor: [65, 105, 225] },
          styles: { fontSize: 8 }
        });
      }
    }
    
    // Footer - Fixed to use internal.getNumberOfPages()
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
      doc.text('Canvassed LLC | Confidential', 105, 290, { align: 'center' });
    }
    
    // Save PDF
    doc.save(`billing_${format(selectedWeek, 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF downloaded');
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = direction === 'prev'
      ? subWeeks(selectedWeek, 1)
      : addDays(selectedWeek, 7);
    setSelectedWeek(startOfWeek(newWeek, { weekStartsOn: 1 }));
  };

  const handleGenerateInvoice = async () => {
    if (!currentWeekBilling || !selectedProjectId || !selectedProject || !userProfile?.uid) {
      toast.error('Missing required data to generate invoice');
      return;
    }

    // Weeks normally lock Monday, but a recap you just imported lands in the
    // current (open) week by import date — so don't hard-block it. Confirm and
    // proceed: the invoice is a DRAFT that routes to the info@ preview before
    // the client sees anything, and each carry-over is marked billed once so
    // regenerating (or Monday's run) can't double-bill.
    if (isCurrentWeek) {
      const ok = window.confirm(
        "This week hasn't closed yet (weeks lock Monday). Generate an invoice now for the validations imported so far this week?\n\nIt will be a draft you preview and approve before anything reaches the client, and each lead is billed only once."
      );
      if (!ok) return;
    }

    setGeneratingInvoice(true);
    try {
      // Get organization data for client info
      const organization = await organizationService.get(selectedProject.organizationId);
      if (!organization) {
        throw new Error('Organization not found');
      }

      // Carry-over adjustments: determination changes THIS week for leads
      // gathered in a PRIOR week (not in this week's base set → no double-bill).
      const baseLeadIds = new Set((currentWeekBilling.leads || []).map((l: any) => l.leadId));

      // Bug-3 guard: only issue a CREDIT (valid→non-valid) for a lead that was
      // actually billed on a previously SENT invoice. Otherwise a born-valid
      // lead (capture bug) that the recap corrects generates a phantom credit
      // for money never charged. Build the set of already-billed parent names
      // from prior sent/paid/overdue invoices (valid line items + carry-overs).
      const priorInvoices = await invoiceService.getInvoices({ projectId: selectedProjectId });
      const billedParentNames = new Set<string>();
      for (const inv of priorInvoices) {
        if (!['sent', 'paid', 'overdue'].includes(inv.status)) continue;
        const detail = inv.billingDetail;
        if (!detail) continue;
        for (const l of detail.leads || []) {
          if (l.determination === 'valid' && (l.amount || 0) > 0 && l.parentName) {
            billedParentNames.add(l.parentName.trim().toLowerCase());
          }
        }
        for (const a of detail.adjustments || []) {
          if (a.kind === 'carryover' && a.parentName) {
            billedParentNames.add(a.parentName.trim().toLowerCase());
          }
        }
      }
      const wasBilled = (name?: string) =>
        !!name && billedParentNames.has(name.trim().toLowerCase());

      // Skip adjustments already swept onto a previous invoice (billed flag) —
      // otherwise regenerating, or a script-generated catch-up invoice, would
      // double-bill the same carry-overs on the next weekly invoice.
      const carryoverPairs = (determinationChanges || [])
        .filter((c: any) => !c.billed && !baseLeadIds.has(c.leadId))
        .map((c: any) => {
          const students = c.students ?? 1;
          if (c.newDetermination === 'valid' && c.previousDetermination !== 'valid') {
            return { changeId: c.id, adj: { leadId: c.leadId, parentName: c.parentName || 'Lead', kind: 'carryover' as const, students } };
          }
          if (c.previousDetermination === 'valid' && c.newDetermination !== 'valid') {
            // Credit ONLY if this lead was actually billed on a prior sent invoice.
            if (!wasBilled(c.parentName)) return null;
            return { changeId: c.id, adj: { leadId: c.leadId, parentName: c.parentName || 'Lead', kind: 'credit' as const, students } };
          }
          return null;
        })
        .filter(Boolean) as Array<{ changeId?: string; adj: { leadId: string; parentName: string; kind: 'carryover' | 'credit'; students: number } }>;
      const carryover = carryoverPairs.map(p => p.adj);

      // Prepare billing period data (base leads + carry-over adjustments)
      const billingPeriod = {
        projectId: selectedProjectId,
        projectName: selectedProject.name,
        periodStart: currentWeekBilling.periodStart,
        periodEnd: currentWeekBilling.periodEnd,
        summary: {
          validLeads: currentWeekBilling.summary.validLeads,
          validStudents: currentWeekBilling.summary.validStudents,
          totalBillable: currentWeekBilling.summary.totalBillable
        },
        leads: currentWeekBilling.leads || [],
        adjustments: carryover,
      };

      // Create the invoice
      const invoice = await invoiceService.createInvoiceFromBilling(
        billingPeriod,
        organization.id,
        organization.name,
        {
          clientName: organization.contactName || organization.name,
          clientEmail: organization.contactEmail,
          clientAddress: organization.address?.street,
          clientCity: organization.address?.city,
          clientState: organization.address?.state,
          clientZip: organization.address?.zipCode
        },
        billRate,
        userProfile.uid
      );

      // Mark the swept determination changes as billed so a regenerate — or a
      // later weekly invoice that reloads them — can't double-bill the same
      // carry-overs. (carryoverPairs carries each change's id for exactly this.)
      const billedChangeIds = carryoverPairs
        .map(p => p.changeId)
        .filter((id): id is string => !!id);
      if (billedChangeIds.length) {
        try {
          await billingAdjustmentsService.markAdjustmentsAsBilled(billedChangeIds);
        } catch (markErr) {
          console.error('Failed to mark determination changes as billed:', markErr);
        }
      }

      // Email the PREVIEW to info@canvassed.com for approval — do NOT send to
      // the client here. Approving from that email is what sends it to the
      // client (with the PDF attached).
      try {
        const previewFn = httpsCallable(getFunctions(), 'sendInvoicePreviewCallable');
        await previewFn({ invoiceId: invoice.id });
        toast.success(`Invoice ${invoice.invoiceNumber} created — preview emailed to info@canvassed.com for approval.`);
      } catch (previewErr) {
        console.error('Failed to send invoice preview:', previewErr);
        toast.success(`Invoice ${invoice.invoiceNumber} created as a draft. Open Invoices to send the preview.`);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  if (isFieldUser) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">My Earnings</h1>
          {selectedProject && (
            <p className="text-gray-600 mb-6">Project: {selectedProject.name}</p>
          )}

          {selectedProjectId && fieldPayments.length > 0 && (
            <div className="space-y-4">
              {fieldPayments
                .filter(payment => payment.userId === userProfile?.uid)
                .map(payment => (
                  <div key={payment.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">
                          Week of {format(payment.weekStart, 'MMM d, yyyy')}
                        </h3>
                        <p className="text-sm text-gray-600">{payment.projectName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          ${payment.totalEarned.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">Earned this week</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Leads</p>
                        <p className="text-xl font-semibold">{payment.totalLeads}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Valid students</p>
                        <p className="text-xl font-semibold text-green-600">{payment.validStudents}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Students</p>
                        <p className="text-xl font-semibold">{payment.totalStudents}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Conversion</p>
                        <p className="text-xl font-semibold">{payment.conversionRate.toFixed(0)}%</p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-600">50% Weekly Payment</p>
                          <p className="font-semibold">${payment.weeklyPaymentDue.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">50% Holdback</p>
                          <p className="font-semibold">${payment.holdbackAmount.toFixed(2)}</p>
                        </div>
                        {payment.holdbackReleased ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                            Holdback released
                          </span>
                        ) : payment.meetsThreshold ? (
                          <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                            Eligible — holdback pending release
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                            Below 20% — holdback held
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Billing & Payments</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track lead conversions, billing, and field staff payments
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project
              </label>
              <div className="px-3 py-2 border rounded-md bg-gray-50 text-gray-900">
                {selectedProject?.name || 'No project selected'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Week
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateWeek('prev')}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="flex-1 text-center">
                  {format(selectedWeek, 'MMM d, yyyy')}
                </span>
                <button
                  onClick={() => navigateWeek('next')}
                  className="p-2 hover:bg-gray-100 rounded"
                  disabled={isCurrentWeek}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {canViewFieldPayments && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  View Mode
                </label>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as 'billing' | 'field')}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="billing">Client Billing</option>
                  <option value="field">Field Payments</option>
                </select>
              </div>
            )}

            <div className="flex items-end space-x-2">
              {/* Exports include the bill rate, so gate them to fiscal viewers
                  (provider_admin / org primary admin) — consistent with the
                  "Total Billed" card gating. Prevents client_admin from
                  exporting internal rate data. */}
              {canViewFiscalDetails && (
                <>
                  <button
                    onClick={exportToExcel}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center"
                    disabled={!currentWeekBilling}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                    disabled={!currentWeekBilling}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    PDF
                  </button>
                </>
              )}
              {canViewFiscalDetails && (
                <button
                  onClick={handleGenerateInvoice}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center justify-center disabled:opacity-50"
                  disabled={!currentWeekBilling || generatingInvoice}
                  title={isCurrentWeek ? "This week hasn't closed yet — you'll be asked to confirm" : undefined}
                >
                  {generatingInvoice ? (
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Receipt className="h-4 w-4 mr-2" />
                  )}
                  Invoice
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">
                {currentWeekBilling?.summary.totalLeads || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Children</p>
              <p className="text-2xl font-bold text-blue-600">
                {currentWeekBilling?.summary.validStudents || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Valid Leads (Parents)</p>
              <p className="text-2xl font-bold text-green-600">
                {currentWeekBilling?.summary.validLeads || 0}
              </p>
            </div>
            {canViewFiscalDetails && (
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Billed</p>
                <p className="text-2xl font-bold text-purple-600">
                  ${((currentWeekBilling?.summary.totalBillable || 0) * billRate + netAdjustmentImpact * billRate).toFixed(2)}
                </p>
              </div>
            )}
            <div className="text-center">
              <p className="text-sm text-gray-600">Conversion Rate</p>
              <p className="text-2xl font-bold text-purple-600">
                {(currentWeekBilling?.summary.conversionRate || 0).toFixed(0)}%
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading billing data...</p>
          </div>
        ) : (
          <>
            {viewMode === 'billing' && canViewBilling ? (
              <div className="space-y-6">
                {/* Determination Changes */}
                {determinationChanges.length > 0 && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                      <h2 className="text-xl font-semibold flex items-center">
                        Determination Changes
                        <span className="ml-2 text-sm text-gray-500">
                          ({determinationChanges.length} changes • Net impact: {netAdjustmentImpact >= 0 ? '+' : ''}{netAdjustmentImpact} students)
                        </span>
                      </h2>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {determinationChanges.map((change, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-4">
                              {change.previousDetermination === 'valid' && change.newDetermination === 'invalid' ? (
                                <ArrowDownCircle className="h-8 w-8 text-red-500" />
                              ) : (
                                <ArrowUpCircle className="h-8 w-8 text-green-500" />
                              )}
                              <div>
                                <p className="font-medium">{change.parentName || 'Unknown Parent'}</p>
                                <p className="text-sm text-gray-600">
                                  {change.previousDetermination} → {change.newDetermination}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Changed by {change.changedByName || change.changedBy} on {format(change.changedAt.toDate(), 'MMM d, h:mm a')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">
                                {change.billingImpact > 0 ? '+' : ''}{change.billingImpact} students
                              </p>
                              <p className="text-sm text-gray-600">
                                ${Math.abs(change.billingImpact * billRate).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Billing Leads Table */}
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b">
                    <h2 className="text-xl font-semibold">Lead Details</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Children</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Captured By</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Determination</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentWeekBilling?.leads.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-8 text-gray-500">
                              No leads for this week
                            </td>
                          </tr>
                        ) : (
                          currentWeekBilling?.leads.map(lead => (
                            <tr key={lead.leadId}>
                              <td className="px-6 py-4 whitespace-nowrap">{lead.parentName}</td>
                              <td className="px-6 py-4">
                                <div>
                                  <p className="text-sm font-medium">{lead.children.length} children</p>
                                  <p className="text-xs text-gray-500">
                                    {lead.children.map(c => c.name).join(', ')}
                                  </p>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {lead.capturedByName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {formatSafeDate(lead.capturedDate, 'MMM d')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  lead.currentDetermination === 'valid' 
                                    ? 'bg-green-100 text-green-800'
                                    : lead.currentDetermination === 'invalid'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {lead.currentDetermination}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {lead.status}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Previous Weeks Summary */}
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b">
                    <h2 className="text-xl font-semibold">Previous Weeks</h2>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      {previousWeeks.map(week => (
                        <div key={format(week.periodStart, 'yyyy-MM-dd')} className="border rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">
                                Week of {format(week.periodStart, 'MMM d, yyyy')}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {week.summary.totalLeads} leads • {week.summary.validLeads} valid • 
                                {week.summary.validStudents || 0} students
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold">
                                ${((week.summary.totalBillable || 0) * billRate).toFixed(2)}
                              </p>
                              <p className="text-sm text-gray-600">
                                {week.summary.conversionRate?.toFixed(0) || 0}% conversion
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : viewMode === 'field' && canViewFieldPayments ? (
              <div className="space-y-6">
                {/* Field User Filter */}
                <div className="bg-white rounded-lg shadow p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Field Staff
                  </label>
                  <select
                    value={selectedFieldUserId}
                    onChange={(e) => setSelectedFieldUserId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="all">All Field Staff</option>
                    {fieldUsers.map(user => (
                      <option key={user.uid} value={user.uid}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Field Payments Table */}
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Field Staff Payments</h2>
                    <button
                      onClick={handleGeneratePayments}
                      className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      title="Compute this week's 50% weekly pay + 50% holdback per contractor"
                    >
                      Generate this week
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Member</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Week</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid students</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv %</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earned</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">50% Pay</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Holdback</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {fieldPayments
                          .filter(payment =>
                            selectedFieldUserId === 'all' || payment.userId === selectedFieldUserId
                          )
                          .filter(payment =>
                            format(payment.weekStart, 'yyyy-MM-dd') === format(selectedWeek, 'yyyy-MM-dd')
                          )
                          .map(payment => (
                            <tr key={payment.id}>
                              <td className="px-6 py-4 whitespace-nowrap">{payment.userName}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {format(payment.weekStart, 'MMM d')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{payment.totalLeads}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{payment.validStudents}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {payment.conversionRate.toFixed(0)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                ${payment.totalEarned.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                ${payment.weeklyPaymentDue.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                ${payment.holdbackAmount.toFixed(2)}
                                {payment.holdbackReleased && <span className="ml-1 text-green-600">(released)</span>}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {payment.meetsThreshold ? (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                    20%+ Conv
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                                    &lt;20% Conv
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {payment.holdbackReleased ? (
                                  <span className="text-gray-400">—</span>
                                ) : payment.meetsThreshold ? (
                                  <button
                                    onClick={() => handleReleaseHoldback(payment.id)}
                                    className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                                  >
                                    Release holdback
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-xs">Held</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}